import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import {
  useAddressRemoteStore,
  ShippingAddress,
} from "./use-address-remote-store";

export type { ShippingAddress };

export const useAddressStore = () => {
  const queryClient = useQueryClient();
  const { isUserAuthenticated } = useAuthContext();
  const {
    fetchUserAddresses,
    fetchDefaultAddress,
    save: saveRemote,
    remove: removeRemote,
  } = useAddressRemoteStore();

  const {
    data: addresses,
    refetch: refetchAddresses,
    isLoading,
  } = useQuery({
    queryKey: ["shipping-addresses"],
    queryFn: async () => {
      if (!isUserAuthenticated) {
        return [];
      }
      return await fetchUserAddresses();
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: isUserAuthenticated,
  });

  const {
    data: defaultAddress,
    refetch: refetchDefaultAddress,
    isLoading: isLoadingDefault,
  } = useQuery({
    queryKey: ["default-shipping-address"],
    queryFn: async () => {
      if (!isUserAuthenticated) {
        return null;
      }
      return await fetchDefaultAddress();
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: isUserAuthenticated,
  });

  const saveAddress = async (
    address: Omit<ShippingAddress, "created_at" | "updated_at">
  ) => {
    const result = await saveRemote.mutateAsync(address);
    await queryClient.refetchQueries({ queryKey: ["shipping-addresses"] });
    await queryClient.refetchQueries({ queryKey: ["default-shipping-address"] });
    return result;
  };

  const deleteAddress = async (addressID: string) => {
    await removeRemote.mutateAsync(addressID);
    await queryClient.refetchQueries({ queryKey: ["shipping-addresses"] });
    await queryClient.refetchQueries({ queryKey: ["default-shipping-address"] });
  };

  return {
    addresses: addresses ?? [],
    defaultAddress,
    saveAddress,
    deleteAddress,
    refetchAddresses,
    refetchDefaultAddress,
    isLoading,
    isLoadingDefault,
  };
};
